import React, {useEffect, useState} from "react";
import AppBar from "@material-ui/core/AppBar";
import {ErrorScreen, HeaderToolbar, Loading} from "../../components";
import {Button, Checkbox, InputLabel, Select, TextField} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemIcon from  "@material-ui/core/ListItemIcon";
import Typography from "@material-ui/core/Typography";
import Toolbar from "@material-ui/core/Toolbar";
import {useStore} from "../../store";
import {invalidateAccount} from "../../store/actions";
import FuseAnimate from "../../components/fuse-animate/fuse-animate";
import Card from "@material-ui/core/Card";
import {Controller, useForm} from "react-hook-form";
import FormControl from "@material-ui/core/FormControl";
import MenuItem from "@material-ui/core/MenuItem";
import {NumberService, SkuService, NotifyService} from "../../services";
import ausPhone from "../../utils/aus-phone";
import Accordion from "@material-ui/core/Accordion"
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore"
import ListIcon from '@material-ui/icons/List';
import RoomIcon from '@material-ui/icons/Room';
import { animateScroll as scroll } from "react-scroll"
import moment from "moment";
import {useAuthUser} from "../../components/auth-provider";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkout from "../../components/checkout/checkout";
import {CheckCircleOutline, SentimentVeryDissatisfied} from "@material-ui/icons";
import {Link} from "react-router-dom";
import {isValidEmail} from "../../utils/valid";
import externalLink from "../../utils/external-link";
import {totals} from "../../utils/checkout";

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        marginTop: 16,
        justifyContent: "center",
    },
    card: {
        display: 'flex',
        flexDirection: 'column',
        padding: 24,
        width: '100%',
        maxWidth: 340,
        [theme.breakpoints.up('sm')]: {
            maxWidth: 360,
        },
    },
}));

// Reducer for local storage
const addServiceReducer = (state, action) => {
    switch (action.type) {
        case 'update':
            return {
                ...state,
                ...action.payload,
            }
        default:
            return state
    }
}

// Hook for accessing local storage via the reducer
const useReducer = (reducer, initialState) => {
    const [state, setState] = useState(initialState)

    function dispatch(action) {
        const nextState = reducer(state, action)
        setState(nextState)
    }

    return [state, dispatch]
}

const landLineSKU = 'SET-PTLL-001'

const AddServicePage = ({location}) => {
    const classes = useStyles();

    const [state, dispatch] = useReducer(addServiceReducer, {
        view: 'start',          // current view
        priors: [],             // prior numbers for this account
        number: {               // selected number
            number:'',
            zone: '',
        },
        sku: {
            id: '',
            name: '',
            taxRate: 0,
            price: 0,
            meta: {
                description: '',
                inclusions: {
                    minutes: {
                        quantity: 0,
                        expiryDays: 0,
                    },
                }
            }
        },
        isFetching: false,
        error: null
    });

    const {user} = useAuthUser();

    // Scroll to top on mount
    useEffect( () => {
        document.getElementById('psb').scrollTo(0,0);
    }, [])  // <- empty deps means it runs only on load

    // Get the SKU we'll be using and any prior numbers
    useEffect(() => {
        dispatch({type:'update', payload: {isFetching: true}})
        Promise.all([
            SkuService.getSku(landLineSKU),
            NumberService.getPriorNumbers(user.accountId)
        ])
            .then(results => {
                // Filter prior numbers to only show available numbers
                const priors = results[1].filter(n => { return n.isAvailable});
                dispatch({type:'update', payload: {sku: results[0], priors, isFetching: false}})
            })
            .catch(error => {
                dispatch({type:'update', payload: {error, isFetching: false}})
            })
    }, [user.accountId])

    return (
        <div id="add-service" className={classes.root}>
            <AppBar position="fixed" className={classes.appBar}>
                <HeaderToolbar location={location.pathname}/>
            </AppBar>
            <Toolbar/>

            <main className={classes.content}>
                { state.isFetching ? <Loading/> : null }
                { state.error && <ErrorScreen error={state.error}/> }
                { state.error === null && !state.isFetching ?
                    <>
                        {state.view === 'start' ?
                            <ItemCard pageState={state} dispatch={dispatch}/>
                            : null
                        }
                        {state.view === 'priors' ?
                            <PriorNumbersCard pageState={state} dispatch={dispatch}/>
                            : null
                        }
                        {state.view === 'choose' ?
                            <ChooseNumberCard pageState={state} dispatch={dispatch}/>
                            : null
                        }
                        {state.view === 'terms' ?
                            <TermsCard pageState={state} dispatch={dispatch}/>
                            : null
                        }
                        {state.view === 'checkout' ?
                            <CheckoutCard pageState={state} dispatch={dispatch}/>
                            : null
                        }
                        {state.view === 'success' ?
                            <SuccessPanel pageState={state}/>
                            :null
                        }
                        {state.view === 'error' ?
                            <ApiErrorPanel error={state.error}/>
                            :null
                        }
                    </>
                    : null
                }
            </main>

        </div>

    );
}

const ItemCard = ({pageState, dispatch}) => {
    const classes = useStyles();

    const sku = pageState.sku;
    const minutes = pageState.sku.meta.inclusions.minutes;

    const handleNew = () => {
        dispatch({ type: 'update', payload: {view: 'choose'}})
    }

    const handlePriors = () => {
        dispatch({ type: 'update', payload: {view: 'priors'}})
    }

    const expiryDate = moment().add(minutes.expiryDays, "days").format('D MMMM YYYY')

    return (
        <FuseAnimate animation="transition.expandIn" delay={100}>
            <div className="flex flex-grow flex-col items-center">
                <Card className={classes.card}>
                    <Typography variant="h6">Add a Landline number</Typography>
                    <hr className="my-2"/>

                    <div className="flex flex-row mx-auto my-6">
                        <div className="text-xl text-gray-500 pt-3">$</div>
                        <div className="text-8xl font-bold px-1">{sku.price/100}</div>
                        <div className="text-xl text-gray-500 self-end pb-1">setup</div>
                    </div>

                    <hr/>

                    <div className="w-full py-4">
                        <ul className="list-disc mx-4">
                            <li className="pb-3">
                                One-off fee includes landline number setup and <span className="whitespace-nowrap">
                                <strong>{minutes.quantity} call minutes</strong></span>
                            </li>
                            <li className="pb-3">
                                Call minutes expire in <strong>{minutes.expiryDays} days</strong>.
                                <span className="whitespace-nowrap">({expiryDate})</span>
                            </li>
                            <li className="pb-3">
                                Unused call minutes roll-over when you recharge before expiry.
                            </li>
                            <li className="pb-3">
                                Landline number may be removed from your account
                                if you do not recharge before call minutes expire.
                            </li>
                        </ul>
                    </div>

                    { pageState.priors.length > 0 ?
                        <div className="w-full pb-4">
                            <Button
                                variant="contained"
                                color="primary"
                                className="w-full"
                                onClick={handlePriors}
                            >
                                Recover a previous number
                            </Button>
                        </div>
                        : null
                    }

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNew}
                    >
                        { pageState.priors.length > 0 ? 'Choose a New Number' : 'Choose Number' }
                    </Button>
                </Card>
            </div>
        </FuseAnimate>
    )
}

const ChooseNumberCard = ({dispatch}) => {
    const [state, setState] = useState({
        zones:      [],
        zoneId:     '',
        regions:    [],
        regionId:   '',
        numbers:    [],
        selected:   {number: '', zone:''},
        isFetching: false,
        error:      null,
    })

    const [expanded, setExpanded] = useState('')

    // handleClick sets the selected number and progresses to the terms view
    const handleClick = () => {
        dispatch({type:'update', payload:{view:'terms', number: state.selected}})
    }

    // handleAccordionChange ensures only one accordion is expanded at a time.
    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : '');
    }

    // handleNumberSelect is called when the user selects
    // a number from the 'other numbers' list
    const handleNumberSelect = (value) => () => {
        setState({...state, selected: value})
        // scroll to the top of the page so we can see the selected number
        scroll.scrollToTop({containerId: 'psb', smooth: true, duration: 300});
    }

    // fetchMoreNumbers fetches another batch of numbers when the
    const fetchMoreNumbers = (regionId) => {
        setState({...state, isFetching: true})
        NumberService.getNumbers(regionId)
            .then(numbers => {
                const selected = numbers.length > 0 ? numbers[0] : {number:'', zone:''};
                setState({...state, numbers, selected, isFetching: false})
                // scroll to the top of the page so we can see the selected number
                scroll.scrollToTop({containerId: 'psb', smooth: true, duration: 300});
            })
            .catch(error => {
                setState({...state, error, isFetching: false})
            })
    }

    // Load zones & initial number set
    useEffect(()=>{
        setState(state =>({...state, isFetching: true}));

        const {zoneId, regionId} = zoneDefaults(); // {zoneId:'vic', regionId:'vic03'}

        // Retrieve zones and a default set of numbers
        Promise.all([
            NumberService.getRegions(),
            NumberService.getNumbers(regionId)
        ]).then(results => {
            // results contains [zones, numbers]
            const [zones, numbers] = results
            let selected = {number:'', zone:''}
            if (numbers.length > 0) {
                selected = numbers[0]
            }
            const regions = selectedZone(zoneId, zones).regions

            setState(state => ({
                ...state,
                zones,
                zoneId,
                numbers,
                selected,
                regions,
                regionId,
                isFetching: false,
                error: null,
            }))
        }).catch(error => {
             setState(state => ({...state, error, isFetching: false}));
        })
    }, [])  // run on load

    // Load more numbers when the regionId changes
    useEffect(() => {
        if (state.regionId !== '') {
            setState(state => ({...state, isFetching: true}));
            NumberService.getNumbers(state.regionId)
                .then(numbers => {
                    const selected = numbers.length > 0 ? numbers[0] : {number:'', zone:''};
                    setState(state => ({...state, numbers, selected, isFetching: false}))
                    // scroll to the top of the page so we can see the selected number
                    scroll.scrollToTop({containerId: 'psb', smooth: true, duration: 300});
                })
                .catch(error => {
                    setState(state => ({...state, error, isFetching: false}));
                })
        }
    }, [state.regionId])

    const regionName = selectedRegionName(state);

    // selectedZone returns the zone object identified by zoneId
    const selectedZone = (zoneId, zones) => {
        if (zoneId !== '' || zones.length > 0) {
            // find the zone
            const zone = zones.filter(z => {
                return z.id === zoneId
            })
            if (zone.length > 0) {
                return zone[0]
            }
        }
        return {id: '', name: '', regions: []}
    }

    return (
        <FuseAnimate
            animation="transition.expandIn"
            delay={100}
        >
            <div className="flex flex-grow flex-col items-center">
                { state.selected.number !== '' && state.error === null && !state.isFetching ?
                    <div className="flex flex-col w-full items-center p-5 bg-white shadow-md rounded-t">
                        <div className="mt-4 mb-6 text-base text-center">Your landline number from the {regionName} region will be:</div>
                        <div className="my-2 text-2xl font-bold">{ausPhone(state.selected.number)}</div>
                        <div className="mb-6 text-base">{state.selected.zone}</div>
                        <div className="my-4 w-full">
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={state.isFetching}
                                className="w-full"
                                onClick={handleClick}
                            >
                                Continue
                            </Button>
                        </div>
                    </div>
                    : null
                }

                { state.selected.number === '' && state.error === null && !state.isFetching ?
                    <NotificationCard regionId={state.regionId} regionName={regionName}/>
                    : null
                }

                { state.selected.number !== '' && state.error === null && !state.isFetching ?
                    <Accordion
                        expanded={expanded === 'more'}
                        onChange={handleAccordionChange('more')}
                        className="w-full"
                        disabled={state.selected.number === ''}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                            <div className="flex flex-row items-center">
                                <ListIcon fontSize="large" color="secondary"/>
                                <Typography variant="body2" className="pl-4">
                                    Show other landline numbers from {regionName}
                                </Typography>
                            </div>
                        </AccordionSummary>
                        <AccordionDetails>
                            <div className="flex-col w-full">
                                <List>

                                {state.numbers.map(number => (
                                    <ListItem
                                        key={number.number}
                                        role={undefined}
                                        dense
                                        button
                                        onClick={handleNumberSelect(number)}
                                    >
                                        <ListItemIcon style={{minWidth: 0}}>
                                            <Checkbox
                                                edge="start"
                                                disableRipple
                                                tabIndex={-1}
                                                checked={number.number === state.selected.number}
                                            />
                                        </ListItemIcon>
                                        <ListItemText>
                                            <span className="font-bold text-xl">{ausPhone(number.number)}</span>
                                            <span className="pl-2">({number.zone})</span>
                                        </ListItemText>
                                    </ListItem>
                                ))}
                                </List>

                                <div className="w-full px-2">
                                <Button
                                    variant="contained"
                                    onClick={() => fetchMoreNumbers(state.regionId)}
                                    className="w-full"
                                    disabled={state.isFetching}
                                >
                                    Show Different Numbers
                                </Button>
                                </div>
                            </div>
                        </AccordionDetails>
                    </Accordion>
                    : null
                }

                { state.error === null && !state.isFetching ?
                    <Accordion
                        expanded={expanded === 'location'}
                        onChange={handleAccordionChange('location')}
                        className="w-full"
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                            <div className="flex flex-row items-center">
                                <RoomIcon fontSize="large" color="secondary"/>
                                <Typography variant="body2" className="pl-4">
                                    Choose a different location
                                </Typography>
                            </div>

                        </AccordionSummary>
                        <AccordionDetails>
                            <LocationSelect state={state} setState={setState} />
                        </AccordionDetails>
                    </Accordion>
                    : null
                }

                { state.error !== null ?
                    <ApiErrorPanel error={state.error}/>
                    : null
                }

                { state.isFetching ?
                    <Loading/>
                    : null
                }
            </div>
        </FuseAnimate>
    )

}

const LocationSelect = ({state, setState}) => {

    // Handles the change event on the State list box
    const onZoneChange = (data) => {
        const zoneId = data.target.value || ''; // Get the state e.g. 'vic'
        const regionId = zoneId + '01'          // Get the first child region e.g. 'vic01'
        const zone = state.zones.filter(e => {return e.id === zoneId });
        if (zone.length > 0) {
            setState({...state, zoneId, regionId, regions: zone[0].regions});
        }
    }

    const onRegionChange = (data) => {
        const regionId = data.target.value || '';
        setState({...state, regionId});
        if (regionId !== '') {
            // Scroll to the top of screen to show the new number
            scroll.scrollToTop({containerId: 'psb', smooth: true, duration: 300});
        }
    }

    return (
        <div className="flex flex-grow flex-col items-center">
            <FormControl
                variant="outlined"
                margin="dense"
                className="w-full"
            >
                <InputLabel id="zone-select-label">Choose a state</InputLabel>
                <Select
                    labelId="zone-select-label"
                    id="zoneSelect"
                    name="zoneSelect"
                    label="Choose a state"
                    defaultValue={state.zoneId}
                    value={state.zoneId}
                    onChange={onZoneChange}
                >
                    { state.zones.map((value, index) => (
                        <MenuItem key={value.id} value={value.id}>
                            {value.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl
                variant="outlined"
                margin="dense"
                className="w-full"
            >
                <InputLabel id="region-select-label">Choose a region</InputLabel>
                <Select
                    labelId="region-select-label"
                    id="regionSelect"
                    name="regionSelect"
                    label="Choose a region"
                    defaultValue={state.regionId}
                    value={state.regionId}
                    onChange={onRegionChange}
                >
                    { state.regions.map((value, index) => (
                        <MenuItem key={value.id} value={value.id}>
                            {value.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </div>
    )
}

const PriorNumbersCard = ({pageState, dispatch}) => {

    const [state, setState] = useState({
        selected: {
            number: pageState.priors[0].number,
            zone: pageState.priors[0].zone,
        }})

    const handleClick = () => {
        dispatch({type:'update', payload:{number: state.selected, view:'terms'}})
    }

    // handleNumberSelect is called when the user selects
    // a number from the 'other numbers' list
    const handleNumberSelect = (value) => () => {
        setState({...state, selected: value})
        // scroll to the top of the page so we can see the selected number
        scroll.scrollToTop({containerId: 'psb', smooth: true, duration: 300});
    }

    return (
        <div className="flex flex-grow flex-col items-center">
            <FuseAnimate
                animation="transition.expandIn"
                delay={100}
            >
                <div className="flex flex-grow flex-col items-center">
                    <div className="flex flex-col w-full items-center p-5 bg-white shadow-md rounded-t">

                        <div className="mt-4 mb-6 text-base text-center">Your landline number will be:</div>
                        <div className="my-2 text-2xl font-bold">{ausPhone(state.selected.number)}</div>
                        <div className="mb-6 drop-shadow-md">{state.selected.zone}</div>
                        <div className="my-4 w-full">
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={state.isFetching}
                                className="w-full"
                                onClick={handleClick}
                            >
                                Continue
                            </Button>
                        </div>
                    </div>

                    { pageState.priors.length > 0 ?
                        <Accordion
                            // expanded={expanded === 'more'}
                            // onChange={handleAccordionChange('more')}
                            className="w-full"
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                                <div className="flex flex-row items-center">
                                    <ListIcon fontSize="large" color="secondary"/>
                                    <Typography variant="body2" className="pl-4">
                                        Show other available landline numbers you've had before
                                    </Typography>
                                </div>
                            </AccordionSummary>
                            <AccordionDetails>
                                <div className="flex-col w-full">
                                    <List>

                                        {pageState.priors.map(number => (
                                            <ListItem
                                                key={number.number}
                                                role={undefined}
                                                dense
                                                button
                                                onClick={handleNumberSelect(number)}
                                            >
                                                <ListItemIcon style={{minWidth: 0}}>
                                                    <Checkbox
                                                        edge="start"
                                                        disableRipple
                                                        tabIndex={-1}
                                                        checked={number.number === state.selected.number}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText>
                                                    <span className="font-bold text-xl">{ausPhone(number.number)}</span>
                                                    <span className="pl-2">({number.zone})</span>
                                                </ListItemText>
                                            </ListItem>
                                        ))}
                                    </List>

                                </div>
                            </AccordionDetails>
                        </Accordion>
                        : null
                    }

                </div>
            </FuseAnimate>
        </div>
    )
}

const NotificationCard = ({regionId, regionName}) => {
    const {handleSubmit, control} = useForm({ mode: 'onChange' });
    const [state, setState] = useState({success: false})

    // onSubmit is called by ReactHookForm when the form is submitted and valid.
    // It returns a promise that is used by RHF to set the isSubmitting flag.
    const onSubmit = (data) => {
        return new Promise((resolve) => {
            const request = {
                comment: data.comment,
                email: data.email,
                regionId: regionId,
            }
            NotifyService.addRequest(request)
                .then(() => {
                    // Notification has been created display success message
                    setState({...state, email: data.email, success: true})
                })
                .catch(error => {
                    setState({...state, error})
                })
                .finally(() => {
                    resolve() // Resolve the promise so RHF knows we're done.
                })
        })
    }

    return (
    <div className="flex flex-col w-full p-5 bg-white shadow-md rounded-t">
        { !state.success && !state.error ?
            <>
            <Typography variant="h6">Out of Stock</Typography>

            <div className="mt-4 mb-2">Sorry! We're out of numbers from the <strong>{regionName}</strong> region at the moment.</div>

            <form
                name="accountForm"
                noValidate
                className="flex flex-col justify-center w-full my-2"
                onSubmit={handleSubmit(onSubmit)}
            >
                <div className="mb-2">Enter your email address and we'll notify you as soon as we have numbers available.</div>

                <Controller
                    name="email"
                    control={control}
                    defaultValue=""
                    rules={{
                        validate: value => isValidEmail(value) || value === '' || 'Please enter a valid email address',
                        required: 'Please enter your email address',
                    }}
                    render={ ({field: {onChange, value}, fieldState:{error} }) => (
                        <TextField
                            type="email"
                            label="Email Address"
                            variant="outlined"
                            value={value}
                            onChange={onChange}
                            error={!!error}
                            helperText={error ? error.message : null}
                            // className={classes.formField}
                            margin="dense"
                        />
                    )}
                />

                <div className="my-2">If you need a number in a specific area or town, let us know where.</div>

                <Controller
                    name="comment"
                    control={control}
                    defaultValue=""
                    render={ ({field: {onChange, value}, fieldState:{error} }) => (
                        <TextField
                            label="I need a number for ..."
                            variant="outlined"
                            value={value}
                            onChange={onChange}
                            margin="dense"
                        />
                    )}
                />

                <div className="mt-4 w-full">
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={state.isFetching}
                        className="w-full"
                    >
                        NOTIFY ME
                    </Button>
                </div>
            </form>
            </>
            : null
        }

        {state.success ?
            <div className="flex flex-col items-center w-full">
                <FuseAnimate animation="callout.bounce" delay={400}>
                    <div className="h-16 my-6">
                        <CheckCircleOutline style={{fontSize: 64, color: 'green'}} />
                    </div>
                </FuseAnimate>
                <FuseAnimate animation="transition.expandIn" delay={100}>
                    <Typography variant="body1" align="center" paragraph>
                        Thanks - we'll email you at <strong>{state.email}</strong> when we have numbers back in stock.
                    </Typography>
                </FuseAnimate>
                <div className="mt-4 w-full">
                    <Button  color="primary" aria-label="NEXT" className="w-full" component={Link} to="/home">
                        DONE
                    </Button>
                </div>
            </div>
            : null
        }

        { state.error && !state.success ?
            <ApiErrorPanel error={state.error} />
            : null
        }

    </div>
    )
}

const TermsCard = ({pageState, dispatch}) => {
    const classes = useStyles();

    const sku = pageState.sku;
    const minutes = pageState.sku.meta.inclusions.minutes;

    const [state, setState] = useState({termsAccepted: false})

    const handleCheck = (e) => {
        setState({...state, termsAccepted: e.target.checked})
    }

    const handleClick = () => {
        dispatch({type:'update', payload:{view:'checkout'}})
    }

    // const expiryDate = moment().add(28, "days").format('D/M/YY')

    return (
        <FuseAnimate animation="transition.expandIn" delay={100}>
            <div className="flex flex-grow flex-col items-center">
                <Card className={classes.card}>
                    <div className="uppercase text-lg font-semibold">HERE'S WHAT YOU'RE GETTING</div>

                    <div className="w-full py-4">
                        <ul className="list-disc mx-4">
                            <li className="pb-3">
                                <strong>{ausPhone(pageState.number.number)}</strong> will be added to your account
                                automatically after checkout.
                            </li>
                            <li className="pb-3">
                                The ${sku.price/100} setup fee is a once-off charge and
                                includes <strong>{minutes.quantity} call minutes</strong> to use with this number.
                            </li>
                            <li className="pb-3">Unused call minutes will expire
                                in <strong>{minutes.expiryDays}&nbsp;days</strong> unless you recharge before.
                            </li>
                            <li className="pb-3">If you use all your call minutes you need to
                                recharge to keep receiving calls.
                            </li>
                            <li className="pb-3">Landline numbers and call minutes are not refundable.
                            </li>
                            <li className="pb-3">
                                The landline number will be removed from your account
                                if you have zero call minutes for 14 days or more.<br/>After that,
                                you may be able to recover the number but a new setup fee will apply.
                            </li>
                        </ul>

                        <FormControlLabel
                            label="Ok, got it!"
                            className="mt-2"
                            control={
                                <Checkbox name="checkTerms" color="primary" onChange={handleCheck}/>
                            }
                        />
                    </div>


                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleClick}
                        disabled={!state.termsAccepted}
                    >
                        Proceed to checkout
                    </Button>
                </Card>
            </div>
        </FuseAnimate>
    )
}

const CheckoutCard = ({pageState, dispatch}) => {
    const classes = useStyles();

    const {subtotal, taxes, total} = totals(pageState.sku);
    // const subtotal = (pageState.sku.price/100);
    // const taxes = (subtotal * pageState.sku.taxRate/100);
    // const total = subtotal + taxes;

    const order = {
        sku: pageState.sku,
        number: pageState.number,
        subtotal: subtotal,
        taxes: taxes,
        total: total,
    }

    // Get the global dispatch function
    const [, gDispatch] = useStore();

    const onSuccess = (purchase) => {
        // the checkout completed successfully
        dispatch({type:'update', payload: {
                order,
                purchase,
                view:          'success',
            }
        })

        // Invalidate the account in global store so we retrieve
        // the new service, dialActions, account status and paymentMethod if saved.
        gDispatch(invalidateAccount())
    }

    const onError = (error) => {
        // the checkout failed

        // display error panel
        dispatch({type:'update', payload:{order, error, view: 'error'} })
    }

    // The checkout card basically wraps the
    // Checkout component, injecting the SKU to be charged.
    return (
        <FuseAnimate animation="transition.expandIn" delay={100}>
            <div className="flex flex-grow flex-col items-center">
                <Card className={classes.card}>
                    <Checkout
                        order={order}
                        onSuccess={onSuccess}
                        onError={onError}
                    />
                </Card>
            </div>
        </FuseAnimate>
    )
}

const SuccessPanel = ({pageState}) => {
    const classes = useStyles();

    // Scroll to top on mount
    useEffect( () => {
        document.getElementById('psb').scrollTo(0,0);
    }, [])  // <- empty deps means it runs only on load

    const number = ausPhone(pageState.number.number)
    const hasMinutes = pageState.sku.meta.hasMinutes;
    const minutes = pageState.sku.meta.inclusions.minutes.quantity;
    const expiryDays = pageState.sku.meta.inclusions.minutes.expiryDays;
    const receiptUrl = pageState.purchase.purchase.receiptUrl;

    return (
        <FuseAnimate animation="transition.expandIn" delay={100}>
            <div className="flex flex-grow flex-col items-center">
                <Card className={classes.card}>
                    <FuseAnimate animation="callout.bounce" delay={400}>
                        <div className="flex flex-col items-center h-16 my-6">
                            <CheckCircleOutline style={{fontSize: 64, color: 'green'}} />
                        </div>
                    </FuseAnimate>
                    {/*    <div className="flex flex-col items-center h-16 my-6">*/}
                    <div className="text-base mb-3">
                        Thank you for your purchase!
                    </div>
                            <div className="text-base mb-3">
                                Your landline number <span className="whitespace-nowrap font-bold">{number}</span> is
                                set up and ready to forward calls to your mobile.
                            </div>
                    <ul className="list-disc mx-4">
                        <li className="pb-3">
                            You'll see this number as the caller ID when you receive a call via your PhoneThru service.
                        </li>
                        { hasMinutes ?
                            <li className="pb-3">
                                You have <strong>{minutes} call minutes</strong> to use with this number. They're good
                                for <strong>{expiryDays} days</strong>.
                            </li>
                            : null
                        }
                        { pageState.purchase.cardUpdated  ?
                            <li className="pb-3">Your card has been saved. You can change or remove your saved
                                card on the account page.</li>
                            : null
                        }
                    </ul>

                    <div className="my-2 w-full">
                        <Button aria-label="VIEW RECEIPT" className="w-full" color="primary" variant="outlined"
                                onClick={(e) => externalLink(e, receiptUrl, true)}>
                            VIEW RECEIPT
                        </Button>
                    </div>

                        {/*</div>*/}
                    <div className="mt-4 w-full">
                        <Button variant="contained" color="primary" aria-label="NEXT" className="w-full" component={Link} to="/home">
                            RETURN TO THE DASHBOARD
                        </Button>
                    </div>
                </Card>
            </div>
        </FuseAnimate>
    )
}

const ApiErrorPanel = ({error}) => {

    return (
        <FuseAnimate animation="transition.expandIn" delay={100}>
            <div className="p-4">
                <div className="flex justify-center text-6xl mt-1 mb-4">
                    <SentimentVeryDissatisfied fontSize="inherit" color="error" />
                </div>
                <Typography paragraph>
                    Sorry, it looks like there's a problem at our end. Please try again later.
                </Typography>
                <Typography variant="body2" color="textSecondary">{error.status}/{error.statusText}</Typography>
            </div>
        </FuseAnimate>
    )
}

const zoneDefaults = () => {
    let tz = '';
    try {
        tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {}
    switch (tz) {
        case 'Australia/Melbourne': return {zoneId: 'vic', regionId: 'vic01'};
        case 'Australia/Brisbane':  return {zoneId: 'qld', regionId: 'qld01'};
        case 'Australia/Lindeman':  return {zoneId: 'qld', regionId: 'qld01'};
        case 'Australia/Sydney':    return {zoneId: 'nsw', regionId: 'nsw01'};
        case 'Australia/Lord_Howe': return {zoneId: 'nsw', regionId: 'nsw01'};
        case 'Australia/Hobart':    return {zoneId: 'tas', regionId: 'tas01'};
        case 'Australia/Adelaide':  return {zoneId: 'sa', regionId: 'sa01'};
        case 'Australia/Perth':     return {zoneId: 'wa', regionId: 'wa01'};
        case 'Australia/Eucla':     return {zoneId: 'wa', regionId: 'wa01'};
        case 'Australia/Darwin':    return {zoneId: 'nt', regionId: 'nt01'};
        case 'Australia/Canberra':  return {zoneId: 'act', regionId: 'act01'};
        case 'Australia/ACT':       return {zoneId: 'act', regionId: 'act01'};
        default:                    return {zoneId: 'vic', regionId: 'vic01'};
    }

}

const selectedRegionName = (state) => {
    if (state.regionId === '' || state.zoneId === '' || state.zones.length === 0) {
        return ''
    }
    // find the zone
    const zone = state.zones.filter(z => { return z.id === state.zoneId })
    if (zone.length > 0) {
        // find the region
        const region = zone[0].regions.filter(r => { return r.id === state.regionId})
        if (region.length > 0) {
            return region[0].name
        }
    }
    return ''
}

export default AddServicePage;






